import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { formatarNumero } from '@viewer/report/documentModel'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  type ProgressoDaComparacao,
  classesDoModelo,
  comparacao,
  comparar,
  elementosEscolhidos,
} from './compararNuvem'
import { PREDEFINICOES, type Predefinicao, nomeDaClasse } from './escolha'
import { casosDaNuvem } from './exemplo'
import { NOME_DO_VEREDICTO, mm, percentagem, relatorioCsv, textoDoEixo } from './relatorioTexto'

/** Comparar a nuvem com elementos escolhidos do IFC, e o relatório por
 *  elemento. Substitui o "Desvio ao IFC", que media à caixa de tudo. */

const NOME_DA_FASE: Record<ProgressoDaComparacao['fase'], string> = {
  elementos: 'A escolher os elementos',
  geometria: 'A ler a geometria dos elementos',
  pontos: 'A medir os pontos',
  eixos: 'A ajustar os eixos',
}

const COR_DO_VEREDICTO = {
  conforme: 'text-[var(--color-ok-text)]',
  desvio: 'text-[var(--color-error-text)]',
  'nao-visto': 'text-[var(--color-text-dim)]',
} as const

const inputCls =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[11px] text-[var(--color-text)]'

export default function ComparacaoComIfc({ cloudId, modelId }: { cloudId: string; modelId: string }) {
  const [predef, setPredef] = useState<Predefinicao>('metalica')
  const [material, setMaterial] = useState(PREDEFINICOES[0].material)
  const [classesEscolhidas, setClassesEscolhidas] = useState<string[]>([])
  const [classesDisponiveis, setClassesDisponiveis] = useState<[string, number][] | null>(null)
  const [raioMm, setRaioMm] = useState('100')
  const [tolMm, setTolMm] = useState('20')
  const temCortes = (viewerApi.getWorld()?.renderer?.three.clippingPlanes.length ?? 0) > 0
  const [respeitarCortes, setRespeitarCortes] = useState(true)
  const [progresso, setProgresso] = useState<ProgressoDaComparacao | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const controlo = useRef<AbortController | null>(null)
  const seleccao = useAppStore((s) => s.selectedTargets)
  useSyncExternalStore(comparacao.subscribe, comparacao.versao)
  const resultado = comparacao.de(cloudId)
  const resultadoDoModelo = resultado?.modelId === modelId ? resultado : null

  // A lista de classes só se lê quando é preciso escolher.
  useEffect(() => {
    if (predef !== 'classes' || classesDisponiveis) return
    let vivo = true
    void classesDoModelo(modelId).then((m) => {
      if (!vivo) return
      setClassesDisponiveis([...m.entries()].map(([c, ids]) => [c, ids.length] as [string, number]).sort((a, b) => b[1] - a[1]))
    })
    return () => {
      vivo = false
    }
  }, [predef, modelId, classesDisponiveis])

  const seleccaoDoModelo = seleccao.filter((t) => t.modelId === modelId).map((t) => t.localId)

  const escolher = (p: Predefinicao) => {
    setPredef(p)
    const def = PREDEFINICOES.find((x) => x.id === p)!
    setMaterial(def.material)
    if (p === 'classes' && classesEscolhidas.length === 0) setClassesEscolhidas(['IFCCOLUMN', 'IFCBEAM'])
  }

  const correr = async () => {
    setErro(null)
    setAviso(null)
    const raio = Math.max(Number(raioMm.replace(',', '.')) / 1000, 0.005)
    const tolerancia = Math.max(Number(tolMm.replace(',', '.')) / 1000, 0.001)
    if (!Number.isFinite(raio) || !Number.isFinite(tolerancia)) {
      setErro('O raio e a tolerância têm de ser números, em milímetros.')
      return
    }
    const c = new AbortController()
    controlo.current = c
    try {
      setProgresso({ fase: 'elementos', feito: 0, total: 1 })
      let ids: number[]
      let classePorId: Map<number, string> | undefined
      if (predef === 'seleccao') {
        ids = seleccaoDoModelo
        if (ids.length === 0) throw new Error('Não há elementos seleccionados neste modelo.')
      } else {
        const classes = predef === 'classes' ? classesEscolhidas : PREDEFINICOES.find((x) => x.id === predef)!.classes
        if (classes.length === 0) throw new Error('Escolhe pelo menos uma classe.')
        const e = await elementosEscolhidos(modelId, classes, material)
        ids = e.ids
        classePorId = e.classePorId
        if (ids.length === 0) {
          throw new Error(
            material.trim()
              ? `Nenhum elemento destas classes tem um material que corresponda a "${material}". ${e.semMaterial > 0 ? `${formatarNumero(e.semMaterial)} não têm material nenhum no IFC.` : ''}`
              : 'O modelo não tem elementos destas classes.',
          )
        }
        if (e.semMaterial > 0 && material.trim()) {
          setAviso(`${formatarNumero(e.semMaterial)} elementos destas classes não têm material no IFC e ficaram de fora.`)
        }
      }
      await comparar(
        cloudId,
        modelId,
        ids,
        { raio, tolerancia, respeitarCortes: temCortes && respeitarCortes },
        setProgresso,
        c.signal,
        classePorId,
      )
    } catch (causa) {
      if (!(causa instanceof DOMException && causa.name === 'AbortError')) {
        setErro(causa instanceof Error ? causa.message : String(causa))
      }
    } finally {
      controlo.current = null
      setProgresso(null)
    }
  }

  const irPara = (localId: number) => {
    const alvo = { modelId, localId }
    useAppStore.getState().setSelection(alvo)
    void viewerApi.fitToItems([alvo])
  }

  const exportar = () => {
    if (!resultadoDoModelo) return
    const csv = relatorioCsv(resultadoDoModelo.linhas, resultadoDoModelo.opcoes.tolerancia)
    downloadBlob(csv, 'comparacao-nuvem-ifc.csv', 'text/csv;charset=utf-8')
  }

  const contagem = resultadoDoModelo
    ? {
        desvio: resultadoDoModelo.linhas.filter((l) => l.veredicto === 'desvio').length,
        conforme: resultadoDoModelo.linhas.filter((l) => l.veredicto === 'conforme').length,
        naoVisto: resultadoDoModelo.linhas.filter((l) => l.veredicto === 'nao-visto').length,
      }
    : null

  const casos = casosDaNuvem(cloudId)

  return (
    <div className="mt-2 space-y-1.5 border-t border-[var(--color-border)] pt-2 text-[11px]">
      <div className="font-medium">Comparar com o IFC</div>
      {casos && (
        // Dito às claras: esta nuvem não é um levantamento, e os desvios
        // foram postos de propósito para se ver se o relatório os acha.
        <div className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] p-1.5 text-[10px]">
          <p className="font-medium">Nuvem sintética, gerada a partir do modelo, com ruído de 2 mm.</p>
          <p className="mt-0.5 text-[var(--color-text-muted)]">Desvios postos de propósito:</p>
          <ul className="list-disc pl-4">
            {casos.map((c) => (
              <li key={c.localId}>{c.descricao}</li>
            ))}
          </ul>
        </div>
      )}
      <label className="flex items-center gap-2">
        <span className="w-16 shrink-0 text-[var(--color-text-muted)]">O quê</span>
        <select value={predef} onChange={(e) => escolher(e.target.value as Predefinicao)} className={`min-w-0 flex-1 ${inputCls}`}>
          {PREDEFINICOES.map((p) => (
            <option key={p.id} value={p.id}>
              {p.id === 'seleccao' ? `${p.nome} (${formatarNumero(seleccaoDoModelo.length)})` : p.nome}
            </option>
          ))}
        </select>
      </label>
      {predef === 'classes' && (
        <div className="max-h-28 overflow-y-auto rounded border border-[var(--color-border)] p-1">
          {classesDisponiveis === null && <span className="text-[var(--color-text-muted)]">A ler as classes…</span>}
          {classesDisponiveis?.map(([c, n]) => (
            <label key={c} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={classesEscolhidas.includes(c)}
                onChange={(e) =>
                  setClassesEscolhidas((v) => (e.target.checked ? [...v, c] : v.filter((x) => x !== c)))
                }
              />
              <span className="flex-1">{nomeDaClasse(c)}</span>
              <span className="text-[var(--color-text-dim)]">{formatarNumero(n)}</span>
            </label>
          ))}
        </div>
      )}
      {predef !== 'seleccao' && (
        <label
          className="flex items-center gap-2"
          title="Só entram os elementos cujo material no IFC contenha uma destas palavras, separadas por |. Vazio, entram todos."
        >
          <span className="w-16 shrink-0 text-[var(--color-text-muted)]">Material</span>
          <input value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="qualquer" className={`min-w-0 flex-1 ${inputCls}`} />
        </label>
      )}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1" title="Um ponto só conta para um elemento se estiver a menos disto da sua superfície. O resto é terreno, vizinhos, outra coisa.">
          Raio <input value={raioMm} onChange={(e) => setRaioMm(e.target.value)} className={`w-12 ${inputCls}`} /> mm
        </label>
        <label className="flex items-center gap-1" title="Um elemento está conforme quando 95% dos seus pontos estão a menos disto do projecto.">
          Tolerância <input value={tolMm} onChange={(e) => setTolMm(e.target.value)} className={`w-12 ${inputCls}`} /> mm
        </label>
      </div>
      {temCortes && (
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={respeitarCortes} onChange={(e) => setRespeitarCortes(e.target.checked)} />
          Só os pontos que o corte deixa ver
        </label>
      )}
      {progresso ? (
        <div>
          <div className="h-1.5 overflow-hidden rounded bg-[var(--color-bg)]">
            <div
              className="h-full bg-[var(--color-accent)] transition-[width]"
              style={{ width: `${Math.round((progresso.feito / Math.max(progresso.total, 1)) * 100)}%` }}
            />
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]">
            <span>{NOME_DA_FASE[progresso.fase]}…</span>
            <button type="button" onClick={() => controlo.current?.abort()} className="ml-auto rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]">
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void correr()}
          className="w-full rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
        >
          Comparar
        </button>
      )}
      {erro && <p className="rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-1.5 text-[var(--color-error-text)]">{erro}</p>}
      {aviso && <p className="text-[10px] text-[var(--color-warn-text)]">{aviso}</p>}
      {resultadoDoModelo && contagem && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-[var(--color-text-muted)]">
            {formatarNumero(resultadoDoModelo.linhas.length)} elementos: {formatarNumero(contagem.desvio)} com desvio,{' '}
            {formatarNumero(contagem.conforme)} conformes, {formatarNumero(contagem.naoVisto)} não vistos.{' '}
            {formatarNumero(resultadoDoModelo.pontosAtribuidos)} de {formatarNumero(resultadoDoModelo.pontosNoCorte)} pontos
            {resultadoDoModelo.pontosNoCorte < resultadoDoModelo.pontosVistos ? ' dentro do corte' : ''} ficaram a menos de{' '}
            {mm(resultadoDoModelo.opcoes.raio)} de um elemento escolhido.
            {resultadoDoModelo.pontosDeVizinhos > 0 &&
              ` ${formatarNumero(resultadoDoModelo.pontosDeVizinhos)} ficaram mais perto de elementos vizinhos que não se escolheram, e não contam.`}
            {resultadoDoModelo.truncado && ' A geometria era grande demais e foi lida só em parte.'}
          </p>
          <div className="flex items-center gap-1">
            <span className="text-[var(--color-text-muted)]">Cores</span>
            {(['levantamento', 'desvio'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => comparacao.mostrar(cloudId, m)}
                className={`rounded border px-1.5 py-0.5 ${
                  resultadoDoModelo.modo === m
                    ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] hover:text-[var(--color-text)]'
                }`}
              >
                {m === 'levantamento' ? 'Do levantamento' : 'Do desvio'}
              </button>
            ))}
            <button type="button" onClick={exportar} className="ml-auto rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]">
              Exportar CSV
            </button>
            <button type="button" onClick={() => comparacao.limpar(cloudId)} title="Largar o resultado e voltar às cores do levantamento" className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]">
              Limpar
            </button>
          </div>
          {resultadoDoModelo.modo === 'desvio' && (
            <p className="text-[10px] text-[var(--color-text-muted)]">
              Verde até {mm(resultadoDoModelo.opcoes.tolerancia)}, amarelo até {mm(resultadoDoModelo.opcoes.tolerancia * 3)}, vermelho acima. Cinzento: pontos que não são de nenhum elemento escolhido.
            </p>
          )}
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {resultadoDoModelo.linhas.map((l) => (
              <li key={l.localId}>
                <button
                  type="button"
                  onClick={() => irPara(l.localId)}
                  className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-1 text-left hover:border-[var(--color-accent-border)]"
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{l.nome ?? `#${l.localId}`}</span>
                    <span className={`shrink-0 text-[10px] font-medium ${COR_DO_VEREDICTO[l.veredicto]}`}>{NOME_DO_VEREDICTO[l.veredicto]}</span>
                  </div>
                  <div className="text-[10px] text-[var(--color-text-muted)]">
                    {nomeDaClasse(l.classe)} · {formatarNumero(l.pontos)} pontos
                    {l.veredicto !== 'nao-visto' && (
                      <> · P95 {mm(l.p95)} · média {mm(l.media)} · {percentagem(l.dentro)} dentro</>
                    )}
                  </div>
                  {l.veredicto !== 'nao-visto' && l.eixo && (
                    <div className="text-[10px] text-[var(--color-text-muted)]">{textoDoEixo(l.eixo)}</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
